import { useEffect, useState } from 'react'
import { competitionRank } from '../../shared/rank'
import { api, type SeasonRow, type SeasonWeek } from '../api'
import { Empty, WeekDates } from '../ui'

/*
 * Season, built from reference/design-screenshots/season.png.
 *
 * The week grid is the whole idea: one narrow column per week, every player on
 * screen at once, so you read DOWN a column to see who won a given week and
 * ACROSS a row to see how somebody got where they are. A total on its own says
 * who is ahead; this says why, which is what the arguing is about.
 *
 * Nothing is computed twice. Points, per-week splits and the best week all
 * arrive from /api/season, which is the same computation the standings use -
 * two ways of scoring one season is how the number on this tab ends up
 * disagreeing with the number one tab across.
 */

/*
 * How many week columns the row can hold.
 *
 * At 390px the row has 342px of content and the arithmetic is fixed: a 24px
 * rank, a 50px total, 12px gutters and 26px cells with 4px between them. Six
 * columns leaves 72px for a name, which the condensed display face can still
 * hold; a seventh eats into "GRANDPA". The design was drawn in week three and
 * this only bites in week seven, so the grid shows the most RECENT six and the
 * expansion below still lists every week that has been played. Nothing is
 * hidden - the column labels say which weeks are on screen.
 */
const COLUMNS = 6

/** A published week with games still to be decided. */
const inPlay = (w: SeasonWeek) => w.finals > 0 && w.finals < w.games

export function Season({
  onViewWeek,
  week,
}: {
  onViewWeek?: (weekId: string) => void
  /*
   * Passed in rather than fetched. Season loads its own table, which carries
   * week numbers and no dates, and this is the same line every other screen
   * shows - so it comes from the same place they get it.
   */
  week?: { number: number; opensAt: number; closesAt: number } | null
}) {
  const [data, setData] = useState<{ weeks: SeasonWeek[]; rows: SeasonRow[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    void api
      .season()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'))
  }, [])

  if (error) return <Empty>{error}</Empty>
  if (!data) return <Empty>Loading…</Empty>
  if (data.weeks.length === 0 || data.rows.length === 0) {
    return <Empty>No weeks have been played yet.</Empty>
  }

  const { weeks, rows } = data
  const strip = weeks.slice(-COLUMNS)
  const playing = weeks.filter(inPlay)
  const settled = weeks.filter((w) => !inPlay(w))

  // The rows arrive sorted by total descending, which is what competitionRank
  // requires. Ties share a place and the next player takes the one after the
  // gap: 1, 2, 2, 2, 5. Dense ranking would call that last player third and
  // tell an eight-year-old he is doing better than he is.
  const places = competitionRank(rows.map((r) => r.total))

  /*
   * A player's best week is a SCORE, not one week.
   *
   * The API hands over a single `bestWeekId`, which picks one arbitrarily when
   * two weeks are equal - and the design does not: GRANDPA's 7 and 7 are both
   * tinted, as are MAX's 6 and 6. Taking the score and tinting everything that
   * matches is the only reading where a player cannot have his best week
   * quietly demoted by the order rows came back in.
   *
   * Completed weeks only. A week still being played has games left to lose, so
   * it cannot be anyone's best yet. And a best of nought is not an
   * achievement, which is what gold means here - it gets no tint.
   */
  const bestScore = new Map<string, number>()
  for (const r of rows) {
    let best = 0
    for (const w of weeks) {
      if (!inPlay(w)) best = Math.max(best, r.byWeek[w.id] ?? 0)
    }
    if (best > 0) bestScore.set(r.playerId, best)
  }
  const isBest = (r: SeasonRow, w: SeasonWeek) =>
    !inPlay(w) && r.byWeek[w.id] !== undefined && r.byWeek[w.id] === bestScore.get(r.playerId)

  /*
   * Where each player finished in each week, keyed `weekId|playerId`.
   *
   * Built once for every week rather than on demand inside an expansion,
   * because a place is only meaningful against the whole field and the field
   * is the same list either way.
   */
  const weekPlace = new Map<string, { place: number; tied: boolean }>()
  for (const w of weeks) {
    const order = [...rows].sort((a, b) => (b.byWeek[w.id] ?? 0) - (a.byWeek[w.id] ?? 0))
    const p = competitionRank(order.map((r) => r.byWeek[w.id] ?? 0))
    const shared = new Map<number, number>()
    for (const place of p) shared.set(place, (shared.get(place) ?? 0) + 1)
    order.forEach((r, i) => {
      weekPlace.set(`${w.id}|${r.playerId}`, {
        place: p[i]!,
        tied: (shared.get(p[i]!) ?? 0) > 1,
      })
    })
  }

  return (
    <div className="pb-1">
      <header className="flex items-baseline justify-between gap-3 px-[18px] pt-5 pb-3.5">
        <span className="text-[30px] font-extrabold tracking-[0.03em] uppercase">
          Season
        </span>
        <span className="slate-num text-right text-[10.5px] font-medium tracking-[0.1em] text-mute">
          {settled.length > 0 && `THRU WK ${settled[settled.length - 1]!.number}`}
          {settled.length > 0 && playing.length > 0 && ' · '}
          {playing.length > 0 && (
            <span className="font-bold text-live">
              &#9679; WK {playing.map((w) => w.number).join(', ')} IN PLAY
            </span>
          )}
          <WeekDates week={week ?? null} />
        </span>
      </header>

      {/*
       * The column labels sit outside the cards, on the ground, so the cards
       * stay a stack of players rather than a table with a header row. They
       * are padded to the same 24px/366px content rails the rows use, which is
       * the only reason a 26px cell lines up with the number under it.
       */}
      <div className="mx-3 flex justify-end px-3 pb-1">
        <span className="flex gap-1">
          {strip.map((w) => (
            <span
              key={w.id}
              className={[
                'slate-num w-[26px] text-center text-[9.5px] tracking-[0.06em]',
                inPlay(w) ? 'font-bold text-live' : 'text-faint',
              ].join(' ')}
            >
              {inPlay(w) && <>&#9679;</>}W{w.number}
            </span>
          ))}
        </span>
        <span className="slate-num w-[50px] text-right text-[9.5px] tracking-[0.06em] text-faint">
          PTS
        </span>
      </div>

      <ul className="flex flex-col gap-1.5 px-3">
        {rows.map((r, i) => {
          const isOpen = open === r.playerId
          const leader = places[i] === 1
          return (
            <li
              key={r.playerId}
              /*
               * The leader's card is outlined in gold rather than filled. A
               * fill would make first place the loudest thing on a screen
               * whose whole job is comparison, and gold already means "yours"
               * on the two numerals inside this same row.
               */
              className={['slate-card overflow-hidden', leader ? 'border-gold' : ''].join(' ')}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : r.playerId)}
                aria-expanded={isOpen}
                className="slate-tap flex w-full items-center gap-3 px-3 py-[11.5px] text-left"
              >
                <span
                  className={[
                    'w-6 shrink-0 text-[20px] leading-none font-extrabold',
                    leader ? 'text-gold-text' : 'text-faint',
                  ].join(' ')}
                >
                  {places[i]}
                </span>

                <span className="min-w-0 flex-1 truncate text-[20px] leading-none font-bold tracking-[0.02em] uppercase">
                  {r.name}
                </span>

                <span className="flex shrink-0 items-center">
                  <span className="flex gap-1">
                    {strip.map((w) => (
                      <WeekCell
                        key={w.id}
                        points={r.byWeek[w.id]}
                        live={inPlay(w)}
                        best={isBest(r, w)}
                      />
                    ))}
                  </span>
                  <span
                    className={[
                      'slate-num w-[50px] text-right text-[24px] leading-none font-bold',
                      leader ? 'text-gold-text' : '',
                    ].join(' ')}
                  >
                    {r.total}
                  </span>
                </span>
              </button>

              {isOpen && (
                <ul className="border-t border-hairline bg-ground/60">
                  {weeks.map((w) => (
                    <WeekLine
                      key={w.id}
                      week={w}
                      correct={r.correctByWeek[w.id] ?? 0}
                      rank={weekPlace.get(`${w.id}|${r.playerId}`)}
                      best={isBest(r, w)}
                      onView={onViewWeek}
                    />
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

      <Legend />
    </div>
  )
}

/**
 * One week's points for one player.
 *
 * Three readings, and they are the palette's three jobs: gold on a tint is the
 * player's own best week, mint is a week still being played, slate is a fact
 * about a week that is over. There is no fourth colour for a bad week - the
 * design has no last-place styling and no red anywhere, because a kid who
 * scored three is already looking at the three.
 */
function WeekCell({
  points,
  best,
  live,
}: {
  points: number | undefined
  best: boolean
  live: boolean
}) {
  return (
    <span className="w-[26px] shrink-0">
      <span
        className={[
          'slate-num block rounded-[3px] py-1 text-center text-[12px] leading-none',
          best
            ? 'bg-gold/18 font-bold text-gold-text'
            : live
              ? 'font-medium text-live'
              : 'font-medium text-slate',
        ].join(' ')}
      >
        {/* A player added mid-season has no cell for a week he was not in. */}
        {points ?? '—'}
      </span>
    </span>
  )
}

/** One week inside an expanded player. */
function WeekLine({
  week: w,
  correct,
  rank,
  best,
  onView,
}: {
  week: SeasonWeek
  correct: number
  rank: { place: number; tied: boolean } | undefined
  best: boolean
  onView?: (weekId: string) => void
}) {
  const live = inPlay(w)

  return (
    <li className="flex items-center gap-3 border-t border-hairline py-[13px] pr-3 pl-12 first:border-t-0">
      {/*
       * The place in THAT week, which is the number the grid above cannot
       * show: eight points is a good week or a mediocre one depending
       * entirely on what everybody else did that Saturday.
       */}
      <span
        className={[
          'w-7 shrink-0 text-center text-[24px] leading-none font-extrabold',
          live ? 'text-live' : best ? 'text-gold-text' : 'text-faint',
        ].join(' ')}
      >
        {rank ? `${rank.tied ? 'T' : ''}${rank.place}` : '—'}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-[14px] leading-none font-bold tracking-[0.02em] uppercase">
            Week {w.number}
          </span>
          {best && (
            <span className="slate-num rounded-[3px] bg-gold/18 px-1.5 py-[3px] text-[8.5px] leading-none font-bold tracking-[0.08em] text-gold-text">
              BEST
            </span>
          )}
        </span>

        {/*
         * The detail line never repeats the rank - it is set 24px tall
         * immediately to the left. What it adds is the denominator, and for a
         * week in play the denominator is the honest part: "1 OF 2" beside "8
         * GAMES LEFT" says how little has actually happened, where a bare
         * "1 POINT" reads as a bad week rather than an early one.
         */}
        <span className="slate-num mt-1 block text-[10px] leading-[1.2] tracking-[0.06em] text-mute uppercase">
          {live ? (
            <>
              <span className="font-medium text-live">
                &#9679; {w.finals === 0 ? 'Not started' : `${correct} of ${w.finals} so far`}
              </span>
              {` · ${w.games - w.finals} games left`}
            </>
          ) : (
            `${correct} of ${w.games} correct`
          )}
        </span>
      </span>

      {/*
       * Past weeks are public, so this is a link and not a permission. The
       * screen switcher belongs to the shell; all this knows is which week was
       * asked for.
       *
       * The negative margin is what keeps the 44px target from setting the row
       * height. Without it slate-tap's floor pushed every week row to 70px and
       * the panel grew by a third; the touch area still spills into the 8px of
       * padding either side, which belongs to nothing else.
       */}
      <button
        type="button"
        onClick={() => onView?.(w.id)}
        className="slate-num slate-tap -my-2 flex shrink-0 items-center justify-end text-[10px] tracking-[0.06em] text-faint uppercase"
      >
        View &rarr;
      </button>
    </li>
  )
}

/*
 * Two marks, and the legend exists because they are the two the grid cannot
 * spell out in a 26px cell. It says what the colours MEAN rather than what
 * they are - "best week", not "gold" - so it still reads for the youngest
 * player in the pool.
 */
function Legend() {
  return (
    <div className="mt-4 flex items-center gap-3 px-[18px] pb-5">
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-3 shrink-0 rounded-[3px] bg-gold/18" />
        <span className="slate-num text-[9.5px] tracking-[0.06em] text-mute uppercase">
          Best week
        </span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="slate-num text-[9.5px] leading-none text-live">&#9679;</span>
        <span className="slate-num text-[9.5px] tracking-[0.06em] text-mute uppercase">
          Week in play
        </span>
      </span>
    </div>
  )
}
